import axios from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000"

export interface PrintOrder {
  id: number
  order_number: string
  status: string
  participation_details: {
    id: number
    exhibition_title: string
    exhibitor_name: string
    stand_category_name: string
  }
  created_at: string
  updated_at: string
  created_by: number
  is_postpaid: boolean
  sms_sent: boolean
  order_details: OrderDetail[]
  pricing?: {
    badge_fee: number
    badge_reprint_fee: number
    currency: string
  }
}

export interface OrderDetail {
  id: number
  representation: {
    id: number
    person: {
      id: number
      first_name: string
      last_name: string
      email: string
      phone: string
    }
    role: string
  }
  amount: number
  created_at: string
}

export interface OrderStaff {
  id: number
  order_detail_id: number
  person_id: number
  person_full_name: string
  person_email: string
  person_phone: string
  person_id_number: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_id: number
  stand_category: string
  profile_photo_url: string | null
  has_profile_photo: boolean
  total_badges_printed: number
  has_original_badge: boolean
  last_badge_type: string | null
  last_printed_at: string | null
  can_print_original: boolean
  /** How the most-recent badge for this staff member was printed.
   *  'bulk'   → printed as part of a 2×2 A4 bulk batch
   *  'single' → printed alone on an A6 sheet
   *  null     → no badge yet, or legacy record before tracking was added */
  print_method: 'bulk' | 'single' | null
  /** Convenience flag — true when the latest badge was a bulk-print job */
  is_bulk_printed: boolean
  /** Convenience flag — true when the latest badge was a single-badge (A6) job */
  is_single_printed: boolean
  status: boolean
  created_at: string
  amount: number
  badge_fee: number
  badge_reprint_fee: number
  currency: string
}

export interface OrderStaffResponse {
  success: boolean
  order: {
    id: number
    order_number: string
    status: string
    exhibitor_name: string
    exhibition_title: string
    stand_category: string
    created_at: string
    updated_at: string
    pricing: {
      badge_fee: number
      badge_reprint_fee: number
      currency: string
    }
  }
  staff: OrderStaff[]
  summary: {
    total_staff: number
    staff_with_badges: number
    original_badges: number
    copy_badges: number
    total_badges: number
    /** Number of staff whose most-recent badge was printed via bulk (2×2 A4) */
    bulk_printed: number
    /** Number of staff who still have no badge — awaiting single (A6) printing */
    pending_single_print: number
  }
}

export interface CompletionStatus {
  success: boolean
  order_id: number
  order_number: string
  status: string
  total_staff: number
  completed_badges: number
  completion_percentage: number
  is_complete: boolean
  staff_status: Array<{
    person_name: string
    representation_id: number
    has_badge: boolean
    role: string
  }>
}

// Badge generation request interface for existing endpoint
export interface BadgeGenerationRequest {
  representation_id: number
  print_type: "original" | "copy"
  force_generation?: boolean
  confirmed_fee?: number
}

export interface BadgeGenerationResponse {
  success: boolean
  message: string
  badge_tracking_id?: number
  badge_data?: any
  template_config?: any
  fee_charged?: number
  currency?: string
  requires_payment?: boolean
  reprint_fee?: number
}

// Search parameters interface
export interface OrderSearchParams {
  search?: string
  page_size?: number | string
  page?: number
}

class PrintOrderService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...getExhibitionHeader(),
    };
  }

  async getNonPrintedOrders(params?: OrderSearchParams): Promise<{ orders: PrintOrder[]; count: number }> {
    try {
      const queryParams = new URLSearchParams()

      if (params?.search) {
        queryParams.append("search", params.search)
      }
      if (params?.page_size) {
        queryParams.append("page_size", params.page_size.toString())
      }
      if (params?.page) {
        queryParams.append("page", params.page.toString())
      }

      const url = `${API_BASE_URL}/api/badges/print-orders/non-printed/${queryParams.toString() ? `?${queryParams.toString()}` : ""}`

      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error fetching non-printed orders:", error)
      throw error
    }
  }

  async getPrintedOrders(params?: OrderSearchParams): Promise<{ orders: PrintOrder[]; count: number }> {
    try {
      const queryParams = new URLSearchParams()

      if (params?.search) {
        queryParams.append("search", params.search)
      }
      if (params?.page_size) {
        queryParams.append("page_size", params.page_size.toString())
      }
      if (params?.page) {
        queryParams.append("page", params.page.toString())
      }

      const url = `${API_BASE_URL}/api/badges/print-orders/printed/${queryParams.toString() ? `?${queryParams.toString()}` : ""}`

      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error fetching printed orders:", error)
      throw error
    }
  }

  async getOrderStaffForPrinting(orderId: number): Promise<OrderStaffResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/badges/print-orders/${orderId}/staff/`, {
        headers: this.getAuthHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error fetching order staff:", error)
      throw error
    }
  }

  // Use the existing badge request endpoint with proper parameters
  async generateBadgeForStaff(request: BadgeGenerationRequest): Promise<BadgeGenerationResponse> {
    try {
      console.log("Service: Generating badge with request:", request)
      // Prepare the request data to match the existing endpoint
      const requestData = {
        representation_id: request.representation_id,
        print_type: request.print_type,
        force_generation: request.force_generation || false,
        confirmed_fee: request.confirmed_fee || 0,
      }

      console.log("Service: Sending request data:", requestData)
      const response = await axios.post(`${API_BASE_URL}/api/badges/badge/request/`, requestData, {
        headers: this.getAuthHeaders(),
      })

      console.log("Service: Raw API response:", response.data)
      // The existing endpoint returns different structure, so we need to adapt it
      const data = response.data
      const adaptedResponse = {
        success: data.success,
        message: data.message,
        badge_tracking_id: data.badge_tracking_id,
        badge_data: data.badge_data,
        template_config: data.template_config,
        fee_charged: data.pricing?.fee || data.fee_charged || 0,
        currency: data.pricing?.currency || data.currency || "RWF",
        requires_payment: data.pricing?.payment_required || false,
      }

      console.log("Service: Adapted response:", adaptedResponse)
      return adaptedResponse
    } catch (error: any) {
      console.error("Service: Error generating badge:", error)
      console.error("Service: Error response:", error.response?.data)
      if (error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  // Use the existing badge history endpoint
  async getBadgeHistory(personId: number): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/badges/person/${personId}/badge-history/`, {
        headers: this.getAuthHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error fetching badge history:", error)
      throw error
    }
  }

  async markOrderAsPrinted(orderId: number, forceComplete = false): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/badges/print-orders/${orderId}/mark-printed/`,
        { force_complete: forceComplete },
        { headers: this.getAuthHeaders() },
      )
      return response.data
    } catch (error) {
      console.error("Error marking order as printed:", error)
      throw error
    }
  }

  // Locks the order: status -> 'completed'. Backend already refuses to
  // generate any further badges for a 'completed' order, so this is what
  // actually stops other operators from printing against it again. Call
  // this right after a bulk print run finishes.
  async completePrintOrder(orderId: number): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/badges/print-orders/${orderId}/complete/`,
        {},
        { headers: this.getAuthHeaders() },
      )
      return response.data
    } catch (error) {
      console.error("Error completing print order:", error)
      throw error
    }
  }

  // Super user / admin only: reopens a completed order (status -> 'approved')
  // so it shows up in the printable list again for other operators.
  async revertPrintOrderToPrinting(orderId: number, reason?: string): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/badges/print-orders/${orderId}/revert-to-printing/`,
        reason ? { reason } : {},
        { headers: this.getAuthHeaders() },
      )
      return response.data
    } catch (error) {
      console.error("Error reverting print order to printing:", error)
      throw error
    }
  }

  async checkOrderCompletionStatus(orderId: number): Promise<CompletionStatus> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/badges/print-orders/${orderId}/completion-status/`, {
        headers: this.getAuthHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error checking order completion:", error)
      throw error
    }
  }

  async sendPrintOrderNotification(orderId: number): Promise<{
    success: boolean
    message?: string
    error?: string
    sms_sent?: boolean
    already_notified?: boolean
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/badges/print-orders/${orderId}/send-notification/`,
        {},
        { headers: this.getAuthHeaders() },
      )
      return response.data
    } catch (error: any) {
      console.error("Error sending print order notification:", error)
      if (error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }
}

export const printOrderService = new PrintOrderService()